import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { AppNav } from "../../components/AppNav";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Layout } from "../../components/Layout";
import { Select } from "../../components/Select";
import { Body, Display, Muted } from "../../components/Typography";
import { ApiError, api } from "../../lib/api";
import type {
  DeviceOrderPreviewResponse,
  DeviceOrderResponse,
  DeviceReturnResponse,
  PaymentMethodResponse,
  StoreResponse,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { US_STATES } from "../../lib/usStates";
import styles from "./Shopping.module.css";
import { StoreCardCapture } from "./StoreCardCapture";

// PRD PAYWALL §1 device bullet (amended 2026-10-02): one SKU, $199 + flat shipping + Stripe
// Tax, charged at order time from the store's saved card. Final outside the trial; ordered
// during the free trial the $199 is a refundable deposit (cancel in the trial, return the box).
export const DEVICE_NAME = "Shobdo Edge Regular";
export const DEVICE_SPEC =
  "Raspberry Pi 4B with a 32 GB SD card, pre-imaged and ready to run. Plug it into your store router, power it on, and the setup wizard takes it from there.";

export const dollars = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

type AddressForm = {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
};

const emptyAddress = (): AddressForm => ({
  name: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
});

const addressComplete = (a: AddressForm): boolean =>
  [a.name, a.line1, a.city, a.state, a.postal_code].every((part) => part.trim() !== "");

// Wallet detection drives the deadlock fix (operator decision 2026-08-28): a store with
// no vaulted card gets the inline card capture instead of a pointer at the (unreachable)
// wizard billing step. "unknown" — the wallet couldn't be read (non-admin member, billing
// not configured) — keeps the plain flow, and a 402 still degrades to the capture.
type WalletState = "unknown" | "loading" | "none" | "ready";

const shipToPayload = (a: AddressForm) => ({
  ship_to: {
    name: a.name.trim(),
    line1: a.line1.trim(),
    ...(a.line2.trim() ? { line2: a.line2.trim() } : {}),
    city: a.city.trim(),
    state: a.state.trim(),
    postal_code: a.postal_code.trim(),
    country: "US",
  },
});

export const Shopping = () => {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();

  const [stores, setStores] = useState<StoreResponse[] | null>(null);
  const [storeId, setStoreId] = useState<string>(searchParams.get("store") ?? "");
  const [address, setAddress] = useState<AddressForm>(emptyAddress());
  const [preview, setPreview] = useState<DeviceOrderPreviewResponse | null>(null);
  const [orders, setOrders] = useState<DeviceOrderResponse[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsBilling, setNeedsBilling] = useState(false);
  const [placed, setPlaced] = useState<DeviceOrderResponse | null>(null);
  const [wallet, setWallet] = useState<WalletState>("unknown");
  const [savedCard, setSavedCard] = useState<PaymentMethodResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.listStores();
        if (cancelled) return;
        setStores(list);
        setStoreId((current) => {
          if (current && list.some((s) => s.id === current)) return current;
          return list[0]?.id ?? "";
        });
      } catch {
        if (!cancelled) setStores([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [returnInfo, setReturnInfo] = useState<DeviceReturnResponse | null>(null);

  const loadOrders = useCallback(async (id: string): Promise<void> => {
    try {
      const fetched = await api.listDeviceOrders(id);
      setOrders(fetched);
      if (fetched.some((o) => o.deposit && o.return_due_at && !o.refunded_at)) {
        setReturnInfo(await api.getDeviceReturn());
      }
    } catch {
      // Non-admin members (and transient failures) simply see no history.
      setOrders([]);
    }
  }, []);

  const checkWallet = useCallback(async (id: string): Promise<void> => {
    setWallet("loading");
    try {
      const methods = await api.listPaymentMethods(id);
      setWallet(methods.length === 0 ? "none" : "ready");
    } catch {
      // Non-admin members (403) and unconfigured deployments (503): the order attempt
      // itself still explains what's missing.
      setWallet("unknown");
    }
  }, []);

  useEffect(() => {
    setPreview(null);
    setPlaced(null);
    setError(null);
    setNeedsBilling(false);
    setSavedCard(null);
    if (storeId) {
      void loadOrders(storeId);
      void checkWallet(storeId);
    } else {
      setOrders([]);
      setWallet("unknown");
    }
  }, [storeId, loadOrders, checkWallet]);

  const handleVaulted = (method: PaymentMethodResponse): void => {
    setSavedCard(method);
    setWallet("ready");
    setNeedsBilling(false);
    setError(null);
  };

  const fail = (e: unknown, fallback: string): void => {
    if (e instanceof ApiError) {
      const message = e.body?.error ?? e.message;
      // 402 covers both "no card on file" and "card declined"; only the former means
      // the wallet is empty and the inline capture should take over.
      if (e.status === 402 && message.includes("no payment card")) {
        setNeedsBilling(true);
        setWallet("none");
        setError(message);
        return;
      }
      setError(message);
      return;
    }
    setError(fallback);
  };

  const handlePreview = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!storeId || busy) return;
    setError(null);
    setNeedsBilling(false);
    setBusy(true);
    try {
      setPreview(await api.previewDeviceOrder(storeId, shipToPayload(address)));
    } catch (e) {
      fail(e, "Couldn't price the order.");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (!storeId || busy) return;
    setError(null);
    setBusy(true);
    try {
      const order = await api.createDeviceOrder(storeId, shipToPayload(address));
      setPlaced(order);
      setPreview(null);
      setAddress(emptyAddress());
      await loadOrders(storeId);
    } catch (e) {
      fail(e, "Couldn't place the order.");
    } finally {
      setBusy(false);
    }
  };

  const updateAddress = (patch: Partial<AddressForm>): void => {
    setAddress((prev) => ({ ...prev, ...patch }));
    // The quoted tax depends on the address, so any edit invalidates the quote.
    setPreview(null);
  };

  return (
    <Layout
      nav={
        <AppNav
          trail={[{ label: "Shopping" }]}
          onSignOut={logout}
          userName={user?.display_name ?? user?.email}
          userAvatar={user?.avatar ?? null}
        />
      }
    >
      <main className={styles.content}>
        <header className={styles.head}>
          <Display>Shopping</Display>
          <Body>
            <Muted>
              Order the edge box that runs Shobdo in your store. It ships ready to go — no technical
              setup beyond plugging it in.
            </Muted>
          </Body>
        </header>

        <section className={styles.skuCard} aria-label={DEVICE_NAME}>
          <div className={styles.skuBody}>
            <span className={styles.skuKicker}>Edge device</span>
            <span className={styles.skuName}>{DEVICE_NAME}</span>
            <Body>
              <Muted>{DEVICE_SPEC}</Muted>
            </Body>
          </div>
          <div className={styles.skuPrice}>
            $199 <span className={styles.skuPriceUnit}>+ shipping &amp; tax</span>
          </div>
        </section>

        {placed && (
          <div className={styles.successBanner}>
            <div className={styles.bannerTitle}>Order placed</div>
            <div className={styles.bannerBody}>
              Your {DEVICE_NAME} is on its way —{" "}
              {dollars(placed.price_cents + placed.shipping_cents + placed.tax_cents)} was charged
              to your store's card. A confirmation email is on its way, and we'll email you again
              when it ships.
            </div>
          </div>
        )}

        {error && (
          <div className={styles.banner} role="alert">
            <div className={styles.bannerTitle}>
              {needsBilling ? "Add a payment card first" : "Couldn't complete that"}
            </div>
            <div className={styles.bannerBody}>{error}</div>
            {needsBilling && (
              <div className={styles.bannerBody}>
                Save a card in the form below, then confirm your order.
              </div>
            )}
          </div>
        )}

        {wallet === "none" && storeId && (
          <StoreCardCapture key={storeId} storeId={storeId} onVaulted={handleVaulted} />
        )}

        {savedCard && (
          <div className={styles.successBanner}>
            <div className={styles.bannerTitle}>Card saved</div>
            <div className={styles.bannerBody}>
              {savedCard.last4 ? `The card ending in ${savedCard.last4}` : "Your card"} is now this
              store's payment card — this order will charge it, and your subscription will use it
              when you finish setup.
            </div>
          </div>
        )}

        <form className={styles.form} onSubmit={handlePreview} noValidate>
          <div className={styles.field}>
            <label htmlFor="shopping-store" className={styles.label}>
              Ordering for store
            </label>
            <Select
              id="shopping-store"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              disabled={busy || stores === null || stores.length === 0}
            >
              {stores === null && <option value="">Loading stores…</option>}
              {stores !== null && stores.length === 0 && (
                <option value="">No stores yet — create one first</option>
              )}
              {stores?.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </Select>
          </div>

          <div className={styles.grid2}>
            <div className={styles.field}>
              <label htmlFor="ship-name" className={styles.label}>
                Recipient name
              </label>
              <Input
                id="ship-name"
                value={address.name}
                onChange={(e) => updateAddress({ name: e.target.value })}
                disabled={busy}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="ship-line1" className={styles.label}>
                Street address
              </label>
              <Input
                id="ship-line1"
                value={address.line1}
                onChange={(e) => updateAddress({ line1: e.target.value })}
                disabled={busy}
              />
            </div>
          </div>
          <div className={styles.grid2}>
            <div className={styles.field}>
              <label htmlFor="ship-line2" className={styles.label}>
                Apt / Unit (optional)
              </label>
              <Input
                id="ship-line2"
                value={address.line2}
                onChange={(e) => updateAddress({ line2: e.target.value })}
                disabled={busy}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="ship-city" className={styles.label}>
                City
              </label>
              <Input
                id="ship-city"
                value={address.city}
                onChange={(e) => updateAddress({ city: e.target.value })}
                disabled={busy}
              />
            </div>
          </div>
          <div className={styles.grid2}>
            <div className={styles.field}>
              <label htmlFor="ship-state" className={styles.label}>
                State
              </label>
              <Select
                id="ship-state"
                value={address.state}
                onChange={(e) => updateAddress({ state: e.target.value })}
                disabled={busy}
              >
                <option value="">Select a state…</option>
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className={styles.field}>
              <label htmlFor="ship-zip" className={styles.label}>
                Zipcode
              </label>
              <Input
                id="ship-zip"
                value={address.postal_code}
                onChange={(e) => updateAddress({ postal_code: e.target.value })}
                disabled={busy}
              />
            </div>
          </div>

          {preview && (
            <div className={styles.summary} aria-label="Order total">
              <div className={styles.summaryRow}>
                <span>{DEVICE_NAME}</span>
                <span>{dollars(preview.price_cents)}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Shipping</span>
                <span>{dollars(preview.shipping_cents)}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Tax</span>
                <span>{dollars(preview.tax_cents)}</span>
              </div>
              <div className={styles.summaryTotal}>
                <span>Total</span>
                <span>{dollars(preview.total_cents)}</span>
              </div>
            </div>
          )}

          <p className={styles.fineprint}>
            The total is charged to the store's saved payment card when you confirm. Ordered during
            your free trial, the device price is a fully refundable deposit: cancel in the trial and
            return the box, and the $199 comes back to you (shipping does not). Outside the trial,
            device purchases are final.
          </p>

          <div className={styles.actions}>
            {preview === null ? (
              <Button type="submit" disabled={busy || !storeId || !addressComplete(address)}>
                {busy ? "Pricing…" : "Get price"}
              </Button>
            ) : (
              <Button type="button" onClick={() => void handleConfirm()} disabled={busy}>
                {busy ? "Placing order…" : `Confirm & pay ${dollars(preview.total_cents)}`}
              </Button>
            )}
          </div>
        </form>

        {orders.length > 0 && (
          <section aria-label="Your orders">
            <Body>Your orders</Body>
            <div className={styles.orders}>
              {orders.map((order) => (
                <div key={order.id} className={styles.orderRow}>
                  <span>{new Date(order.created_at).toLocaleDateString()}</span>
                  <span>
                    {DEVICE_NAME}
                    {order.tracking_number && (
                      <span className={styles.orderTracking}>
                        Tracking number: {order.tracking_number}
                      </span>
                    )}
                    {order.deposit && !order.refunded_at && (
                      <span className={styles.orderTracking}>
                        {dollars(order.price_cents)} deposit — refunded if you cancel in your trial
                        and return the box
                      </span>
                    )}
                    {order.refunded_at && order.refund_cents !== null && (
                      <span className={styles.orderTracking}>
                        {dollars(order.refund_cents)} refunded on{" "}
                        {new Date(order.refunded_at).toLocaleDateString()}
                      </span>
                    )}
                    {order.deposit && order.return_due_at && !order.refunded_at && returnInfo && (
                      <span className={styles.orderTracking}>
                        To get the deposit back, ship the box by{" "}
                        {new Date(order.return_due_at).toLocaleDateString()} to{" "}
                        {returnInfo.address_lines.join(", ")}
                        {returnInfo.note ? ` (${returnInfo.note})` : ""}. Return shipping is yours;
                        the refund lands 5 to 10 business days after it arrives.
                      </span>
                    )}
                  </span>
                  <span>{dollars(order.price_cents + order.shipping_cents + order.tax_cents)}</span>
                  <span className={styles.orderStatus}>{order.status}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </Layout>
  );
};
